import React from 'react';
import { Link } from 'react-router-dom';
import { Lightbulb, Users, Clock, ArrowRight } from 'lucide-react';
import { AuthButton } from '../components/AuthButton';

const HomePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-16">
        {/* Header */}
        <div className="flex justify-end mb-8">
          <AuthButton />
        </div>
        
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-6">
            <Lightbulb className="h-16 w-16 text-indigo-600 mr-4" />
            <h1 className="text-5xl font-bold text-gray-900">
              Team Idea Boards
            </h1>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Collaborative retrospective boards that bring your team together. 
            Create anonymous sticky notes, derive action items, and export insights — all in real-time.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
            <Users className="h-12 w-12 text-green-600 mb-4" />
            <h3 className="text-xl font-semibold mb-3">Anonymous Collaboration</h3>
            <p className="text-gray-600">
              Team members can contribute anonymously, encouraging honest feedback and open communication.
            </p>
          </div>
          
          <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
            <Clock className="h-12 w-12 text-blue-600 mb-4" />
            <h3 className="text-xl font-semibold mb-3">Real-time Updates</h3>
            <p className="text-gray-600">
              See changes instantly as team members add sticky notes and action items in real-time.
            </p>
          </div>
          
          <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
            <Lightbulb className="h-12 w-12 text-purple-600 mb-4" />
            <h3 className="text-xl font-semibold mb-3">Multiple Templates</h3>
            <p className="text-gray-600">
              Choose from various retrospective formats like Start/Stop/Continue, WWW/CBI, and more.
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Ready to start your retrospective?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Create a new board in seconds and share it with your team
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/templates"
              className="inline-flex items-center px-8 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              Get Started
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            
            <Link
              to="/teams"
              className="inline-flex items-center px-8 py-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              <Users className="mr-2 h-5 w-5" />
              Go to Retro Teams
            </Link>
          </div>
        </div>

        {/* Demo Section */}
        <div className="mt-20 text-center">
          <h3 className="text-2xl font-semibold text-gray-900 mb-4">
            How it works
          </h3>
          <div className="grid md:grid-cols-4 gap-6 mt-8">
            <div className="text-center">
              <div className="bg-indigo-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-indigo-600">1</span>
              </div>
              <h4 className="font-semibold mb-2">Choose Template</h4>
              <p className="text-sm text-gray-600">Select from our pre-built retrospective templates</p>
            </div>
            
            <div className="text-center">
              <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">2</span>
              </div>
              <h4 className="font-semibold mb-2">Share Board</h4>
              <p className="text-sm text-gray-600">Share the board link with your team members</p>
            </div>
            
            <div className="text-center">
              <div className="bg-yellow-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-yellow-600">3</span>
              </div>
              <h4 className="font-semibold mb-2">Collaborate</h4>
              <p className="text-sm text-gray-600">Add anonymous sticky notes and comments</p>
            </div>
            
            <div className="text-center">
              <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">4</span>
              </div>
              <h4 className="font-semibold mb-2">Export Results</h4>
              <p className="text-sm text-gray-600">Export to Excel or save to team folder</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;